import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { API } from '../../utils/constants';
import { isIntakeImageFile } from '../../utils/helpers';

const IntakePhotoThumbnails = ({ intakeId, files, onOpen, testIdPrefix }) => {
  const [previewUrls, setPreviewUrls] = useState({});
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed

  // Derived list of image-only files — stable reference for keyboard handler
  const imageFiles = useMemo(
    () => (Array.isArray(files) ? files.filter((f) => isIntakeImageFile(f)) : []),
    [files]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft')
        setLightboxIndex((i) => (i - 1 + imageFiles.length) % imageFiles.length);
      if (e.key === 'ArrowRight')
        setLightboxIndex((i) => (i + 1) % imageFiles.length);
    },
    [lightboxIndex, imageFiles.length]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Lock body scroll when lightbox is open (prevents iOS scroll-behind)
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);
  useEffect(() => {
    let isActive = true;
    const loadPreviews = async () => {
      if (!intakeId || imageFiles.length === 0) {
        setPreviewUrls((prev) => {
          Object.values(prev).forEach((url) => url && URL.revokeObjectURL(url));
          return {};
        });
        return;
      }

      const entries = await Promise.all(
        imageFiles.map(async (file) => {
          try {
            const res = await axios.get(`${API}/intakes/${intakeId}/files/${file.id}`, {
              responseType: 'blob',
            });
            return [file.id, URL.createObjectURL(res.data)];
          } catch (err) {
            console.error('Error loading intake thumbnail', err);
            return [file.id, ''];
          }
        })
      );

      if (!isActive) {
        entries.forEach(([, url]) => url && URL.revokeObjectURL(url));
        return;
      }

      setPreviewUrls((prev) => {
        Object.values(prev).forEach((url) => {
          if (url && !entries.find(([, next]) => next === url)) URL.revokeObjectURL(url);
        });
        const next = {};
        entries.forEach(([id, url]) => { if (url) next[id] = url; });
        return next;
      });
    };

    loadPreviews();
    return () => { isActive = false; };
  }, [intakeId, imageFiles]);

  if (!Array.isArray(files) || files.length === 0) return null;

  const handleThumbClick = (file) => {
    if (isIntakeImageFile(file)) {
      const idx = imageFiles.findIndex((f) => f.id === file.id);
      setLightboxIndex(idx >= 0 ? idx : 0);
    } else {
      onOpen(file);
    }
  };

  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i - 1 + imageFiles.length) % imageFiles.length);
  };
  const goNext = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i + 1) % imageFiles.length);
  };

  const currentFile = lightboxIndex !== null ? imageFiles[lightboxIndex] : null;
  const currentUrl = currentFile ? previewUrls[currentFile.id] : null;

  return (
    <>
      {/* Thumbnail grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-photo-thumbnails`}>
        {files.map((file) => {
          const isImage = isIntakeImageFile(file);
          const previewUrl = previewUrls[file.id];
          return (
            <button
              key={file.id}
              type="button"
              onClick={() => handleThumbClick(file)}
              className="text-left group"
              data-testid={`${testIdPrefix}-photo-thumb-${file.id}`}
            >
              <div className="w-full h-20 rounded-md border border-gray-200 overflow-hidden bg-gray-100 flex items-center justify-center relative">
                {isImage && previewUrl ? (
                  <>
                    <img
                      src={previewUrl}
                      alt={file.filename || file.stored_name || 'Intake photo'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      data-testid={`${testIdPrefix}-photo-img-${file.id}`}
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </>
                ) : (
                  <div className="text-[11px] text-gray-500" data-testid={`${testIdPrefix}-photo-placeholder-${file.id}`}>
                    {isImage ? 'Loading...' : 'File'}
                  </div>
                )}
              </div>
              <div className="mt-1 text-[10px] text-gray-500 truncate" data-testid={`${testIdPrefix}-photo-name-${file.id}`}>
                {file.filename || file.stored_name}
              </div>
            </button>
          );
        })}
      </div>

      {/* Lightbox — Portal renders at document.body to escape overflow/transform parents (fixes iOS Safari) */}
      {lightboxIndex !== null && ReactDOM.createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/90"
          style={{ zIndex: 9999 }}
          onClick={closeLightbox}
          data-testid={`${testIdPrefix}-lightbox`}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-5 text-white/70 hover:text-white text-4xl leading-none"
            style={{ zIndex: 10000 }}
            data-testid={`${testIdPrefix}-lightbox-close`}
            aria-label="Close"
          >
            &times;
          </button>

          {/* Counter */}
          {imageFiles.length > 1 && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/60 text-sm select-none">
              {lightboxIndex + 1} / {imageFiles.length}
            </div>
          )}

          {/* Prev arrow */}
          {imageFiles.length > 1 && (
            <button
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-6xl px-2 select-none"
              style={{ zIndex: 10000 }}
              data-testid={`${testIdPrefix}-lightbox-prev`}
              aria-label="Previous photo"
            >
              &#8249;
            </button>
          )}

          {/* Image + filename */}
          <div
            className="flex flex-col items-center gap-3 max-w-[88vw]"
            onClick={(e) => e.stopPropagation()}
          >
            {currentUrl ? (
              <img
                src={currentUrl}
                alt={currentFile?.filename || ''}
                className="max-w-full max-h-[82vh] object-contain rounded-lg shadow-2xl"
                data-testid={`${testIdPrefix}-lightbox-img`}
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center text-white/40 text-sm">
                Loading...
              </div>
            )}
            <p className="text-white/50 text-xs truncate max-w-sm">
              {currentFile?.filename || currentFile?.stored_name}
            </p>
          </div>

          {/* Next arrow */}
          {imageFiles.length > 1 && (
            <button
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-6xl px-2 select-none"
              style={{ zIndex: 10000 }}
              data-testid={`${testIdPrefix}-lightbox-next`}
              aria-label="Next photo"
            >
              &#8250;
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default IntakePhotoThumbnails;
